import styled from 'styled-components';
import {Section, SectionHeader} from '../../../components/Form/FormStyles';
import {EditedProject, visibleFundingParticipations} from '../EditedProject';
import {CommunityTreesPlantedControl} from '../MultipleProjectTypes/CommunityTreesPlantedControl';
import {CommunityTreesPlantedDBHControl} from '../MultipleProjectTypes/CommunityTreesPlantedDBHControl';
import {FundingPartnersControl} from '../MultipleProjectTypes/FundingPartnersControl';
import {ImpactStoryCompletedControl} from '../MultipleProjectTypes/ImpactStoryCompletedControl';
import {ImpactStoryInProgressControl} from '../MultipleProjectTypes/ImpactStoryInProgressControl';
import {ImpactStoryInProgressShortControl} from '../MultipleProjectTypes/ImpactStoryInProgressShortControl';
import {LocationControl} from '../MultipleProjectTypes/LocationControl';
import {MapControl} from '../MultipleProjectTypes/Map/MapControl';
import {NameControl} from '../MultipleProjectTypes/NameControl';
import {ProjectIdControl} from '../MultipleProjectTypes/ProjectIdControl';
import {ProjectStatusControl} from '../MultipleProjectTypes/ProjectStatusControl';
import {ProjectTypeControl} from '../MultipleProjectTypes/ProjectTypeControl';
import {DemoProjectStatusControl} from '../MultipleProjectTypes/DemoProjectStatusControl';
import {ReasonForPlantingControl} from '../MultipleProjectTypes/ReasonForPlantingControl';
import {TreeSpeciesControl} from '../MultipleProjectTypes/TreeSpeciesControl';
import {ImagesSection} from '../MultipleProjectTypes/ImagesSection';
import {ImpactStoryCompletedShortControl} from '../MultipleProjectTypes/ImpactStoryCompletedShortControl';
import {ProjectYearsControl} from '../MultipleProjectTypes/ProjectYearsControl';
import {BenefitsSection} from '../MultipleProjectTypes/BenefitsSection';

const TreeMetricsLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 275px);
    gap: 1rem 1rem;
`;

export function MembershipTreesProject({
    project,
    setProjectAndValidate,
    completedProjectSetProjectAndValidate,
    newProject,
    navigationPromptActive,
    setNavigationPromptActive,
    registerSave,
    registerHasChanges,
    hasChangesUpdated,
    generateShortImpactStory
}: {
    project: EditedProject;
    setProjectAndValidate: (project: EditedProject) => void;
    completedProjectSetProjectAndValidate: (project: EditedProject) => void;
    newProject?: boolean;
    navigationPromptActive: boolean;
    setNavigationPromptActive: (navigationPromptActive: boolean) => void;
    registerSave: (f: (projectId: number) => void) => void;
    registerHasChanges: (f: () => boolean) => void;
    hasChangesUpdated: () => void;
    generateShortImpactStory: (originalText: string) => Promise<string>;
    reloadProject?: () => void;
}) {
    return (
        <>
            <Section>
                <ProjectStatusControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    completedProjectSetProjectAndValidate={completedProjectSetProjectAndValidate}
                />
            </Section>

            <Section>
                <SectionHeader>What is the project?</SectionHeader>
                <ProjectIdControl project={project} />
                <DemoProjectStatusControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <ProjectTypeControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <NameControl project={project} setProjectAndValidate={setProjectAndValidate} />
            </Section>

            <Section>
                <SectionHeader>Where is the project?</SectionHeader>
                <LocationControl project={project} setProjectAndValidate={setProjectAndValidate} />
                <MapControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    registerSave={registerSave}
                    registerHasChanges={registerHasChanges}
                    hasChangesUpdated={hasChangesUpdated}
                    navigationPromptActive={navigationPromptActive}
                    setNavigationPromptActive={setNavigationPromptActive}
                />
            </Section>

            <Section>
                <SectionHeader>When is the project?</SectionHeader>
                <ProjectYearsControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            </Section>

            <Section>
                <SectionHeader>Why is the project being done?</SectionHeader>
                <ReasonForPlantingControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <ImpactStoryInProgressControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <ImpactStoryInProgressShortControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    generateShortImpactStory={generateShortImpactStory}
                />
                <ImpactStoryCompletedControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <ImpactStoryCompletedShortControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    generateShortImpactStory={generateShortImpactStory}
                />
            </Section>

            <Section>
                <SectionHeader>Who is participating in the project?</SectionHeader>
                <FundingPartnersControl
                    participations={visibleFundingParticipations(project)}
                    projectId={project.Id}
                    projectType={project.ProjectType.current}
                    readOnly
                />
            </Section>

            <Section>
                <SectionHeader>What is being planted?</SectionHeader>
                <TreeMetricsLayout>
                    <CommunityTreesPlantedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                    <CommunityTreesPlantedDBHControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                </TreeMetricsLayout>

                <TreeSpeciesControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            </Section>

            <BenefitsSection project={project} />

            <ImagesSection
                newProject={newProject}
                project={project}
                setProjectAndValidate={setProjectAndValidate}
            />
        </>
    );
}
